import { useMemo, useRef, useState } from "react";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from "@/components/ui/command";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useMenuItems, type MenuItemRow } from "@/hooks/useMenuItems";
import { usePackages, type PackageRow } from "@/hooks/usePackages";
import { useDietaryTags, getDietaryTagLabel } from "@/hooks/useDietaryTags";
import { usePricingTypes, getPricingLabel, getUnitLabel } from "@/hooks/usePricingTypes";
import { groupItemsByCategory } from "@/lib/quote-item-categories";
import { lineTotal } from "@/lib/quote-totals";
import { cn } from "@/lib/utils";
import { Package, UtensilsCrossed, PenLine, X } from "lucide-react";

export interface PickedItem {
  name: string;
  description: string;
  pricing_type: string;
  unit_price: number;
  quantity: number;
  line_total: number;
  menu_item_id?: string;
  package_id?: string;
}

interface QuoteItemPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestCount: number;
  serviceType: string;
  onPickItem: (item: PickedItem) => void;
  onAddCustom: (name: string) => void;
}

function formatPrice(price: number) {
  return `$${price.toFixed(2)}`;
}

// Below md (768px, use-mobile.tsx's breakpoint) the picker is full screen and
// never focuses the search box itself: on a phone that raises the keyboard
// over the list (#999). Read at event time, not via useIsMobile(), because
// that hook is false on its first render and Radix auto-focuses before it
// settles when the picker mounts already open.
function isPhoneViewport() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches;
}

export function QuoteItemPicker({ open, onOpenChange, guestCount, serviceType, onPickItem, onAddCustom }: QuoteItemPickerProps) {
  const { data: menuItems } = useMenuItems();
  const { data: packages } = usePackages();
  const { data: pricingTypes } = usePricingTypes();
  const { data: dietaryTags } = useDietaryTags();
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const activeItems = useMemo(() => menuItems?.filter((i) => i.is_active) ?? [], [menuItems]);
  const activePackages = packages?.filter((p) => p.is_active) ?? [];

  // Category filter (#817). A category that vanishes on refetch (its last item
  // deactivated) falls back to All rather than leaving an empty list.
  const groups = useMemo(() => groupItemsByCategory(activeItems), [activeItems]);
  const selected = groups.some((g) => g.id === categoryId) ? categoryId : null;
  const visibleGroups = selected ? groups.filter((g) => g.id === selected) : groups;

  // Every close route clears the filter, so a caller that keeps the picker
  // mounted never reopens it filtered.
  const handleOpenChange = (next: boolean) => {
    if (!next) setCategoryId(null);
    onOpenChange(next);
  };

  const handleSelectMenuItem = (item: MenuItemRow, priceIdx: number) => {
    const price = item.prices?.[priceIdx];
    if (!price) return;
    const pricingType = price.pricing_type;
    const unitPrice = price.price;
    const qty = pricingType === "per_person" ? guestCount : 1;
    onPickItem({
      name: item.name,
      description: item.description ?? "",
      pricing_type: pricingType,
      unit_price: unitPrice,
      quantity: qty,
      line_total: lineTotal(unitPrice, qty),
      menu_item_id: item.id,
    });
    handleOpenChange(false);
    setSearch("");
  };

  const handleSelectPackage = (pkg: PackageRow) => {
    const isPP = pkg.pricing_type === "per_person";
    const qty = isPP ? guestCount : 1;
    onPickItem({
      name: pkg.name,
      description: pkg.description ?? "",
      pricing_type: isPP ? "per_person" : "custom",
      unit_price: pkg.price,
      quantity: qty,
      line_total: lineTotal(pkg.price, qty),
      package_id: pkg.id,
    });
    handleOpenChange(false);
    setSearch("");
  };

  const typed = search.trim();

  const handleCustom = () => {
    onAddCustom(typed);
    handleOpenChange(false);
    setSearch("");
  };

  // Square-style one-off item: never filtered out (forceMount), and it carries
  // whatever was typed as the line's name (#823). Always a plain row at the
  // bottom (#999): cmdk highlights the first item, so on top it read as the
  // main action, and Enter on a catalog match must pick the priced catalog
  // item, not a $0 custom line.
  const customGroup = (
    <CommandGroup forceMount>
      <CommandItem value="__custom__" forceMount onSelect={handleCustom} className="py-1.5">
        <PenLine className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">
            {typed ? `Add "${typed}" as a custom item` : "Add custom line item"}
          </div>
          <div className="text-xs text-muted-foreground">One-off — not saved to your menu</div>
        </div>
      </CommandItem>
    </CommandGroup>
  );

  // Tight rows (#999): name, tags and price on one line, then one line of
  // description and unit. The category badge only shows where a search mixes
  // categories; under a category heading it repeats the heading.
  const renderMenuItem = (item: MenuItemRow, showCategory: boolean) => {
    const prices = item.prices ?? [];
    if (prices.length === 0) {
      return (
        <CommandItem
          key={item.id}
          value={`${item.name} ${item.category?.name ?? ""}`}
          onSelect={() => {}}
          disabled
          className="py-1.5 opacity-50"
        >
          <UtensilsCrossed className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{item.name}</div>
            <div className="text-xs text-muted-foreground">No prices configured</div>
          </div>
        </CommandItem>
      );
    }
    return prices.map((price, pi) => (
      <CommandItem
        key={`${item.id}-${pi}`}
        value={`${item.name} ${item.category?.name ?? ""} ${price.label ?? ""} ${price.pricing_type}`}
        onSelect={() => handleSelectMenuItem(item, pi)}
        className="flex items-start gap-2 py-1.5"
      >
        <UtensilsCrossed className="mt-0.5 h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm truncate">{item.name}</span>
            {showCategory && item.category?.name && (
              <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0 font-normal">
                {item.category.name}
              </Badge>
            )}
            {item.dietary_tags?.map((tag) => (
              <Badge key={tag} variant="outline" className="shrink-0 text-[9px] leading-tight px-1 py-0 font-normal">
                {getDietaryTagLabel(dietaryTags, tag)}
              </Badge>
            ))}
            <span className="ml-auto shrink-0 pl-2 font-semibold text-sm">{formatPrice(price.price)}</span>
          </div>
          <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
            <span className="flex-1 min-w-0 truncate">{item.description}</span>
            <span className="shrink-0 text-[10px]">
              {[getPricingLabel(pricingTypes, price.pricing_type), price.label].filter(Boolean).join(" · ")}
            </span>
          </div>
        </div>
      </CommandItem>
    ));
  };

  const chips = [{ id: null, name: "All" }, ...groups];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Full screen below md (#999), so no quote form shows around it; the
          built-in 16px close X (Content's last child) gives way to a 44px one. */}
      <DialogContent
        ref={contentRef}
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          if (!isPhoneViewport()) return;
          e.preventDefault();
          contentRef.current?.focus();
        }}
        className="flex flex-col overflow-hidden p-0 gap-0 max-w-lg md:max-h-[85vh] max-md:left-0 max-md:top-0 max-md:translate-x-0 max-md:translate-y-0 max-md:h-[100dvh] max-md:max-w-none max-md:!rounded-none max-md:border-0 max-md:data-[state=open]:slide-in-from-left-0 max-md:data-[state=open]:slide-in-from-top-0 max-md:data-[state=closed]:slide-out-to-left-0 max-md:data-[state=closed]:slide-out-to-top-0 max-md:[&>button:last-child]:hidden"
      >
        <DialogTitle className="sr-only">Add item</DialogTitle>
        <Command
          className="flex-1 min-h-0 rounded-lg max-md:rounded-none max-md:[&_[cmdk-item][data-selected=true]]:bg-transparent max-md:[&_[cmdk-item][data-selected=true]]:text-foreground"
          shouldFilter={true}
          // cmdk moves focus to the search box whenever the highlighted row
          // changes while its root has focus, and a tap on a non-button part
          // of the header focuses the root. On a phone, hand focus back to
          // the dialog so a stray tap never raises the keyboard.
          onFocus={(e) => {
            if (e.target === e.currentTarget && isPhoneViewport()) contentRef.current?.focus();
          }}
        >
          <div className="flex items-center">
            <div className="flex-1 min-w-0">
              <CommandInput
                ref={inputRef}
                placeholder="Search menu items, packages..."
                value={search}
                onValueChange={setSearch}
                className="md:pr-8"
              />
            </div>
            <DialogClose asChild>
              {/* Inside the cmdk root, so Enter would otherwise pick the highlighted row. */}
              <button
                type="button"
                aria-label="Close picker"
                onKeyDown={(e) => e.key !== "Tab" && e.stopPropagation()}
                className="md:hidden flex h-11 w-11 shrink-0 items-center justify-center border-b text-muted-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </DialogClose>
          </div>
          {groups.length > 1 && (
            // cmdk handles keys on its root, so Enter on a chip would otherwise
            // select the highlighted item (the first catalog row on an empty search).
            // Tab still bubbles so the dialog's focus trap can wrap it.
            <div
              role="group"
              aria-label="Filter by category"
              className="flex gap-1 overflow-x-auto border-b px-3 py-2"
              onKeyDown={(e) => e.key !== "Tab" && e.stopPropagation()}
            >
              {chips.map((chip) => (
                <button
                  key={chip.id ?? "__all__"}
                  type="button"
                  aria-pressed={selected === chip.id}
                  onClick={() => {
                    setCategoryId(selected === chip.id ? null : chip.id);
                    if (!isPhoneViewport()) inputRef.current?.focus();
                  }}
                  className={cn(
                    "shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selected === chip.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
                  )}
                >
                  {chip.name}
                </button>
              ))}
            </div>
          )}
          <CommandList className="max-md:flex-1 max-md:max-h-none md:max-h-[70vh]">
            <CommandEmpty>No menu items or packages match.</CommandEmpty>

            {/* Menu items: grouped by category on an empty search, one group while
                typing so cmdk ranks matches across categories. */}
            {typed ? (
              <CommandGroup heading={selected ? visibleGroups[0].name : "Menu Items"}>
                {visibleGroups.flatMap((g) => g.items).map((item) => renderMenuItem(item, !selected))}
              </CommandGroup>
            ) : (
              visibleGroups.map((g) => (
                <CommandGroup key={g.id} heading={g.name}>
                  {g.items.map((item) => renderMenuItem(item, false))}
                </CommandGroup>
              ))
            )}

            {/* Packages have no menu category, so a category filter hides them. */}
            {!selected && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Packages">
                  {activePackages.map((pkg) => (
                    <CommandItem
                      key={pkg.id}
                      value={`${pkg.name} package ${pkg.description ?? ""}`}
                      onSelect={() => handleSelectPackage(pkg)}
                      className="flex items-start gap-2 py-1.5"
                    >
                      <Package className="mt-0.5 h-4 w-4 text-accent shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm truncate">{pkg.name}</span>
                          <Badge variant="outline" className="shrink-0 text-[9px] leading-tight px-1 py-0 font-normal">
                            Package
                          </Badge>
                          <span className="ml-auto shrink-0 pl-2 font-semibold text-sm">{formatPrice(pkg.price)}</span>
                        </div>
                        <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
                          <span className="flex-1 min-w-0 truncate">{pkg.description}</span>
                          <span className="shrink-0 text-[10px]">
                            {[
                              pkg.min_guests ? `${pkg.min_guests}–${pkg.max_guests ?? "∞"} guests` : null,
                              pkg.pricing_type === "per_person" ? "Per Person" : "Flat Rate",
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            <CommandSeparator />
            {customGroup}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
